import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Package, Truck, Shield, Clock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../context/CartContext';
import { smallFormatAPI } from '../utils/api';
import { useToast } from '../hooks/use-toast';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import RecentlyViewed from '../components/RecentlyViewed';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { recentlyViewed, addToRecentlyViewed } = useRecentlyViewed();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({
    size: '',
    quantity: '',
    color: '',
    finish: '',
    stock: '',
    turnaround: ''
  });
  const [calculatedPrice, setCalculatedPrice] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const foundProduct = await smallFormatAPI.getProduct(productId);
        
        if (foundProduct) {
          setProduct(foundProduct);
          const defaultOptions = {
            size: foundProduct.options?.sizes?.[0] || '',
            quantity: foundProduct.options?.quantities?.[0] || '',
            color: foundProduct.options?.colors?.[0] || '',
            finish: foundProduct.options?.finishes?.[0] || '',
            stock: foundProduct.options?.stocks?.[0] || '',
            turnaround: foundProduct.options?.turnaround?.[0] || ''
          };
          setSelectedOptions(defaultOptions);
          
          if (foundProduct.pricing && foundProduct.pricing[defaultOptions.quantity]) {
            setCalculatedPrice(foundProduct.pricing[defaultOptions.quantity]);
          } else {
            setCalculatedPrice(foundProduct.basePrice || 0);
          }
          
          // Add to recently viewed
          addToRecentlyViewed({
            ...foundProduct,
            section: 'small-format'
          });
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, addToRecentlyViewed]);

  useEffect(() => {
    if (product && product.pricing && selectedOptions.quantity) {
      const price = product.pricing[selectedOptions.quantity] || product.basePrice;
      setCalculatedPrice(price);
    }
  }, [selectedOptions.quantity, product]);

  const handleOptionChange = (optionType, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionType]: value
    }));
  };

  const handleAddToCart = () => {
    if (!product) return;
    const productWithSection = { ...product, section: 'smallformat' };
    addToCart(productWithSection, selectedOptions, 1);
    toast({
      title: "Added to cart!",
      description: `${product.name} has been added to your cart.`,
      duration: 3000,
    });
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gold-600 animate-spin mx-auto mb-4" />
          <p className="text-ivory-500 font-body">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="p-12 rounded-xl bg-ivory-100 border border-ivory-300/50 text-center shadow-luxury">
          <p className="text-ivory-500 text-lg mb-4 font-body">{error || 'Product not found'}</p>
          <Button asChild className="btn-luxury-primary font-body">
            <Link to="/products">Browse Products</Link>
          </Button>
        </div>
      </div>
    );
  }

  const features = [
    { icon: Package, text: 'Premium quality materials' },
    { icon: Clock, text: 'Fast turnaround time' },
    { icon: Shield, text: 'Satisfaction guaranteed' },
    { icon: Truck, text: 'Fast & reliable shipping' },
  ];

  return (
    <div className="min-h-screen bg-ivory-50">
      <div className="container mx-auto px-4 py-8">
        <Link 
          to="/products" 
          className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-8 transition-colors font-body"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="aspect-square rounded-xl overflow-hidden bg-ivory-100 border border-ivory-300/50 shadow-luxury">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-ivory-800 mb-4" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-lg text-ivory-500 leading-relaxed font-body">
                {product.description}
              </p>
            </div>

            {/* Price */}
            <div className="p-6 rounded-xl gradient-champagne border border-gold-500/20">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold text-gold-600 font-body" data-testid="product-price">
                  ${calculatedPrice.toFixed(2)}
                </span>
              </div>
              <p className="text-sm text-ivory-500 font-body">Price for {selectedOptions.quantity} units</p>
            </div>

            {/* Options */}
            <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50 shadow-luxury">
              <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-6">Configure Your Order</h3>
              <div className="space-y-5">
                {/* Size Selection */}
                <div>
                  <Label htmlFor="size" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Size</Label>
                  <Select value={selectedOptions.size} onValueChange={(value) => handleOptionChange('size', value)}>
                    <SelectTrigger id="size" className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ivory-50 border-ivory-300">
                      {product.options.sizes.map((size) => (
                        <SelectItem key={size} value={size} className="text-ivory-700 hover:bg-gold-500/10 font-body">{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Quantity Selection */}
                <div>
                  <Label htmlFor="quantity" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Quantity</Label>
                  <Select value={selectedOptions.quantity.toString()} onValueChange={(value) => handleOptionChange('quantity', parseInt(value))}>
                    <SelectTrigger id="quantity" className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="quantity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ivory-50 border-ivory-300">
                      {product.options.quantities.map((qty) => (
                        <SelectItem key={qty} value={qty.toString()} className="text-ivory-700 hover:bg-gold-500/10 font-body">
                          {qty} units - ${product.pricing?.[qty]?.toFixed(2) || product.basePrice.toFixed(2)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Color Selection */}
                {product.options.colors && product.options.colors.length > 0 && (
                  <div>
                    <Label htmlFor="color" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Color Options</Label>
                    <Select value={selectedOptions.color} onValueChange={(value) => handleOptionChange('color', value)}>
                      <SelectTrigger id="color" className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="color-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-ivory-50 border-ivory-300">
                        {product.options.colors.map((color) => (
                          <SelectItem key={color} value={color} className="text-ivory-700 hover:bg-gold-500/10 font-body">{color}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Finish Selection */}
                <div>
                  <Label htmlFor="finish" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Finish</Label>
                  <Select value={selectedOptions.finish} onValueChange={(value) => handleOptionChange('finish', value)}>
                    <SelectTrigger id="finish" className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="finish-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ivory-50 border-ivory-300">
                      {product.options.finishes.map((finish) => (
                        <SelectItem key={finish} value={finish} className="text-ivory-700 hover:bg-gold-500/10 font-body">{finish}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Stock Selection */}
                <div>
                  <Label htmlFor="stock" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Stock</Label>
                  <Select value={selectedOptions.stock} onValueChange={(value) => handleOptionChange('stock', value)}>
                    <SelectTrigger id="stock" className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="stock-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ivory-50 border-ivory-300">
                      {product.options.stocks.map((stock) => (
                        <SelectItem key={stock} value={stock} className="text-ivory-700 hover:bg-gold-500/10 font-body">{stock}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Turnaround Selection */}
                {product.options.turnaround && product.options.turnaround.length > 0 && (
                  <div>
                    <Label htmlFor="turnaround" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Turnaround Time</Label>
                    <Select value={selectedOptions.turnaround} onValueChange={(value) => handleOptionChange('turnaround', value)}>
                      <SelectTrigger id="turnaround" className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="turnaround-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-ivory-50 border-ivory-300">
                        {product.options.turnaround.map((time) => (
                          <SelectItem key={time} value={time} className="text-ivory-700 hover:bg-gold-500/10 font-body">{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full text-lg py-6 btn-luxury-primary rounded-lg font-body"
                onClick={handleBuyNow}
                data-testid="buy-now-btn"
              >
                Buy Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full text-lg py-6 btn-luxury-outline rounded-lg font-body"
                onClick={handleAddToCart}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 rounded-lg bg-ivory-100 border border-ivory-300/50"
                >
                  <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-gold-600" />
                  </div>
                  <span className="text-sm text-ivory-600 font-body">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recently Viewed Section */}
        <RecentlyViewed products={recentlyViewed} currentProductId={productId} />
      </div>
    </div>
  );
};

export default ProductDetailPage;
